import { useState, useEffect, useCallback, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ChevronDown,
  Rocket,
  Eye,
  Plus,
  Trash2,
  Save,
  Upload,
  FolderOpen,
  Table,
  ShieldCheck,
} from "lucide-react"
import { useApcmOpsStore } from "../stores/apcmOpsStore"
import * as api from "../api/apcm-ops.api"
import type {
  BulkInitResult,
  PreflightCheck,
  SQSQueueHealth,
  VoiceProviderReadiness,
  BulkIngestionJob,
  BulkIngestionJobSummary,
} from "../types/apcm-ops.types"
import type { ConsentCSVResult } from "../api/apcm-ops.api"
import { cn } from "@/lib/utils"
import { CarePlanGenerationTracker } from "./CarePlanGenerationTracker"
import { parseApiTimestamp } from "@/lib/datetime"

interface DeploymentReadinessProps {
  orgId: string
  onRefresh: () => void
}

function StatusIcon({ status }: { status: "pass" | "fail" | "warn" }) {
  if (status === "pass")
    return <CheckCircle2 className="h-5 w-5 text-success shrink-0" />
  if (status === "warn")
    return <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
  return <XCircle className="h-5 w-5 text-destructive shrink-0" />
}

function QueueStatus({ label, health }: { label: string; health: SQSQueueHealth }) {
  const status = health.reachable ? "pass" : "fail"
  return (
    <div className="flex items-center gap-2">
      <StatusIcon status={status} />
      <span className="text-sm">
        {label}:{" "}
        {health.reachable ? (
          <span className="text-muted-foreground">
            reachable ({health.messages_available ?? 0} msgs, {health.messages_in_flight ?? 0} in flight)
          </span>
        ) : (
          <span className="text-destructive">unreachable — {health.error}</span>
        )}
      </span>
    </div>
  )
}

/**
 * Will this org's scheduled calls actually dial? Calls go out through Retell
 * when the scheduler fires; the old pipecat call queue is gone. The backend
 * runs the same dispatch guard over every pending call, so a caller ID that
 * is not marked RETELL shows up here the evening before, not as a no-show.
 */
function VoiceProviderStatus({ voice }: { voice: VoiceProviderReadiness }) {
  const { pending_calls: pending } = voice
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <StatusIcon status={voice.ready ? "pass" : "fail"} />
        <span className="text-sm font-medium">Retell dial-out</span>
        <span className="text-xs text-muted-foreground">
          {pending.total === 0
            ? "no calls scheduled"
            : `${pending.would_dial} of ${pending.total} scheduled calls will dial`}
        </span>
      </div>

      {voice.problems.length > 0 && (
        <ul className="ml-7 space-y-1">
          {voice.problems.map((p) => (
            <li key={p} className="text-xs text-destructive">
              {p}
            </li>
          ))}
        </ul>
      )}

      {pending.blocked.length > 0 && (
        <ul className="ml-7 space-y-1">
          {pending.blocked.map((b) => (
            <li key={b.reason} className="text-xs text-muted-foreground">
              <span className="font-semibold text-destructive tabular-nums">{b.count}</span> calls:{" "}
              {b.reason}
            </li>
          ))}
        </ul>
      )}

      {voice.caller_ids.length > 0 && (
        <div className="ml-7 flex flex-wrap gap-1.5">
          {voice.caller_ids.map((n) => (
            <span
              key={n.phone_number}
              className={cn(
                "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium",
                n.registered_in_retell
                  ? "border-success/30 bg-success/10 text-success"
                  : "border-border bg-muted/40 text-muted-foreground",
              )}
              title={`provider: ${n.provider ?? "unset"}`}
            >
              {n.phone_number}
              {n.is_default && <span className="opacity-70">default</span>}
              {!n.registered_in_retell && <span className="opacity-70">not in Retell</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export function DeploymentReadiness({ orgId, onRefresh }: DeploymentReadinessProps) {
  const { preflightCheck, preflightLoading, fetchPreflightCheck, executeAction } =
    useApcmOpsStore()

  const [dryRunResult, setDryRunResult] = useState<BulkInitResult | null>(null)
  const [dryRunLoading, setDryRunLoading] = useState(false)
  const [initLoading, setInitLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [failuresOpen, setFailuresOpen] = useState(false)
  const [allFailuresOpen, setAllFailuresOpen] = useState(false)
  const [stuckOpen, setStuckOpen] = useState(false)

  // Approved PCPs state
  const [pcps, setPcps] = useState<string[]>([])
  const [pcpDraft, setPcpDraft] = useState<string[]>([])
  const [newPcp, setNewPcp] = useState("")
  const [pcpLoading, setPcpLoading] = useState(false)
  const [pcpSaving, setPcpSaving] = useState(false)
  const [pcpDirty, setPcpDirty] = useState(false)

  // Consent CSV state
  const [csvResult, setCsvResult] = useState<ConsentCSVResult | null>(null)
  const [csvUploading, setCsvUploading] = useState(false)
  const [qmbLoading, setQmbLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Bulk Drive ingestion state
  const [driveFolderId, setDriveFolderId] = useState("")
  const [driveRecursive, setDriveRecursive] = useState(false)
  const [driveMaxConcurrent, setDriveMaxConcurrent] = useState(5)
  const [driveSubmitting, setDriveSubmitting] = useState(false)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [activeJob, setActiveJob] = useState<BulkIngestionJob | null>(null)
  const [recentJobs, setRecentJobs] = useState<BulkIngestionJobSummary[]>([])
  const [jobResultsOpen, setJobResultsOpen] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const loadPcps = useCallback(async () => {
    setPcpLoading(true)
    try {
      const res = await api.getApprovedPCPs(orgId)
      setPcps(res.approved_pcps)
      setPcpDraft(res.approved_pcps)
      setPcpDirty(false)
    } catch (err) {
      console.error("Failed to load PCPs:", err)
    } finally {
      setPcpLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    loadPcps()
  }, [loadPcps])

  const handleSavePcps = async () => {
    setPcpSaving(true)
    try {
      const res = await api.updateApprovedPCPs(orgId, pcpDraft)
      setPcps(res.approved_pcps)
      setPcpDraft(res.approved_pcps)
      setPcpDirty(false)
    } catch (err) {
      console.error("Failed to save PCPs:", err)
    } finally {
      setPcpSaving(false)
    }
  }

  const addPcp = () => {
    const trimmed = newPcp.trim()
    if (trimmed && !pcpDraft.includes(trimmed)) {
      setPcpDraft([...pcpDraft, trimmed])
      setNewPcp("")
      setPcpDirty(true)
    }
  }

  const removePcp = (index: number) => {
    setPcpDraft(pcpDraft.filter((_, i) => i !== index))
    setPcpDirty(true)
  }

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setCsvUploading(true)
    setCsvResult(null)
    try {
      const result = await api.uploadConsentedCSV(orgId, file)
      setCsvResult(result)
      onRefresh()
    } catch (err: any) {
      console.error("CSV upload failed:", err)
    } finally {
      setCsvUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  // Load recent jobs on mount
  useEffect(() => {
    api.getBulkIngestionJobs(orgId).then(setRecentJobs).catch(() => {})
  }, [orgId])

  // Poll active job
  useEffect(() => {
    if (!activeJobId) return
    const poll = () => {
      api.getBulkIngestionJob(activeJobId).then((job) => {
        setActiveJob(job)
        if (job.status === "completed" || job.status === "failed") {
          if (pollRef.current) clearInterval(pollRef.current)
          pollRef.current = null
          api.getBulkIngestionJobs(orgId).then(setRecentJobs).catch(() => {})
        }
      }).catch(() => {})
    }
    poll()
    pollRef.current = setInterval(poll, 5000)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [activeJobId, orgId])

  const extractFolderId = (input: string): string => {
    // Handle full URLs like https://drive.google.com/drive/folders/FOLDER_ID
    const match = input.match(/folders\/([a-zA-Z0-9_-]+)/)
    return match ? match[1] : input.trim()
  }

  const handleDriveIngest = async () => {
    const folderId = extractFolderId(driveFolderId)
    if (!folderId) return
    setDriveSubmitting(true)
    try {
      const res = await api.ingestSimplifiedFromDrive(orgId, folderId, {
        recursive: driveRecursive,
        maxConcurrent: driveMaxConcurrent,
      })
      setActiveJobId(res.job_id)
      setActiveJob(null)
    } catch (err) {
      console.error("Drive ingestion failed:", err)
    } finally {
      setDriveSubmitting(false)
    }
  }

  const pf = preflightCheck

  const handleRunChecks = () => fetchPreflightCheck(orgId)

  const handleDryRun = async () => {
    setDryRunLoading(true)
    try {
      const result = await api.initializeAllPatients(orgId, true)
      setDryRunResult(result)
    } catch (err: any) {
      console.error("Dry run failed:", err)
    } finally {
      setDryRunLoading(false)
    }
  }

  const handleInitialize = async () => {
    setConfirmOpen(false)
    setInitLoading(true)
    try {
      await executeAction("Initialize All Patients", async () => {
        await api.initializeAllPatients(orgId, false)
      })
      setDryRunResult(null)
      await fetchPreflightCheck(orgId)
      onRefresh()
    } finally {
      setInitLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-3">
          <CardTitle className="text-base font-semibold">Deployment Readiness</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRunChecks}
            disabled={preflightLoading}
            className="gap-2"
          >
            {preflightLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Run Checks
          </Button>
        </CardHeader>

        <CardContent>
          {!pf ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Click "Run Checks" to evaluate deployment readiness
            </p>
          ) : (
            <div className="space-y-4">
              {/* Overall banner */}
              {pf.all_checks_passed && (
                <div className="rounded-lg border border-success/30 bg-success/10 p-3 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  <span className="text-sm font-medium text-success">
                    All checks passed — system is ready for deployment
                  </span>
                </div>
              )}

              <div className="divide-y">
                {/* 1. Patient Coverage */}
                <div className="py-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <StatusIcon
                      status={pf.patients_without_state === 0 ? "pass" : "fail"}
                    />
                    <span className="text-sm font-medium">
                      Patient Coverage: {pf.patients_with_state}/{pf.total_patients} initialized
                    </span>
                    {pf.patients_without_state > 0 && (
                      <Badge variant="outline" className="text-destructive border-destructive/30 ml-auto">
                        {pf.patients_without_state} need initialization
                      </Badge>
                    )}
                  </div>

                  {pf.patients_without_state > 0 && (
                    <div className="ml-7 space-y-2">
                      {dryRunResult && (
                        <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                            State Preview
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(dryRunResult.state_breakdown).map(
                              ([state, count]) => (
                                <Badge key={state} variant="secondary" className="text-xs">
                                  {count} → {state}
                                </Badge>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDryRun}
                          disabled={dryRunLoading}
                          className="gap-1.5"
                        >
                          {dryRunLoading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                          Dry Run
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setConfirmOpen(true)}
                          disabled={initLoading}
                          className="gap-1.5"
                        >
                          {initLoading ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Rocket className="h-3 w-3" />
                          )}
                          Initialize All Patients
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* 2. Voice provider + workflow queue */}
                <div className="py-3 space-y-2">
                  {pf.voice_provider ? (
                    <VoiceProviderStatus voice={pf.voice_provider} />
                  ) : (
                    <div className="flex items-center gap-2">
                      <StatusIcon status="warn" />
                      <span className="text-sm text-muted-foreground">
                        Retell dial-out: this API version does not report it
                      </span>
                    </div>
                  )}
                  <QueueStatus label="Workflow queue" health={pf.sqs_health.workflow_queue} />
                </div>

                {/* 3. Recent Failures */}
                <div className="py-3">
                  <Collapsible open={failuresOpen} onOpenChange={setFailuresOpen}>
                    <div className="flex items-center gap-2">
                      <StatusIcon
                        status={
                          pf.recent_failures_24h === 0
                            ? "pass"
                            : pf.recent_failures_24h <= 5
                              ? "warn"
                              : "fail"
                        }
                      />
                      <span className="text-sm font-medium">
                        {pf.recent_failures_24h === 0
                          ? "No failures in last 24h"
                          : `${pf.recent_failures_24h} failure${pf.recent_failures_24h > 1 ? "s" : ""} in last 24h`}
                      </span>
                      {pf.recent_failures_24h > 0 && pf.last_failures.length > 0 && (
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="ml-auto h-6 px-2">
                            <ChevronDown
                              className={cn(
                                "h-3.5 w-3.5 transition-transform",
                                failuresOpen && "rotate-180",
                              )}
                            />
                          </Button>
                        </CollapsibleTrigger>
                      )}
                    </div>
                    <CollapsibleContent>
                      <div className="ml-7 mt-2 space-y-1.5">
                        {pf.last_failures.slice(0, 5).map((f) => (
                          <div
                            key={f.id}
                            className="rounded border bg-muted/30 p-2 text-xs space-y-0.5"
                          >
                            <div className="flex justify-between">
                              <span className="font-medium">{f.agent_name}</span>
                              <span className="text-muted-foreground">{f.updated_at}</span>
                            </div>
                            {f.error_message && (
                              <p className="text-muted-foreground truncate">{f.error_message}</p>
                            )}
                          </div>
                        ))}
                        {pf.last_failures.length > 5 && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full h-7 text-xs"
                            onClick={() => setAllFailuresOpen(true)}
                          >
                            View all {pf.last_failures.length} failures
                          </Button>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>

                {/* 4. Stuck Patients */}
                <div className="py-3">
                  <Collapsible open={stuckOpen} onOpenChange={setStuckOpen}>
                    <div className="flex items-center gap-2">
                      <StatusIcon
                        status={pf.stuck_patients_count === 0 ? "pass" : "fail"}
                      />
                      <span className="text-sm font-medium">
                        {pf.stuck_patients_count === 0
                          ? "No stuck patients"
                          : `${pf.stuck_patients_count} patient${pf.stuck_patients_count > 1 ? "s" : ""} stuck > 4h`}
                      </span>
                      {pf.stuck_patients_count > 0 && (
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="ml-auto h-6 px-2">
                            <ChevronDown
                              className={cn(
                                "h-3.5 w-3.5 transition-transform",
                                stuckOpen && "rotate-180",
                              )}
                            />
                          </Button>
                        </CollapsibleTrigger>
                      )}
                    </div>
                    <CollapsibleContent>
                      <div className="ml-7 mt-2 space-y-1.5">
                        {pf.stuck_patients.map((sp) => (
                          <div
                            key={sp.patient_id}
                            className="rounded border bg-muted/30 p-2 text-xs flex justify-between"
                          >
                            <span className="font-medium">
                              {sp.patient_name ?? sp.patient_id}
                            </span>
                            <div className="flex gap-2 items-center">
                              <Badge variant="secondary" className="text-[10px]">
                                {sp.current_state}
                              </Badge>
                              <span className="text-muted-foreground">since {sp.since}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>

                {/* 5. State Distribution */}
                {Object.keys(pf.state_distribution).length > 0 && (
                  <div className="py-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                      Current State Distribution
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(pf.state_distribution)
                        .sort(([, a], [, b]) => b - a)
                        .map(([state, count]) => (
                          <Badge key={state} variant="outline" className="text-xs">
                            {state}: {count}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Initialize All Patients</DialogTitle>
            <DialogDescription>
              This will create APCM state rows for {pf?.patients_without_state ?? 0} patients
              based on their existing consent and care plan data. This action is idempotent
              (running it again is safe).
            </DialogDescription>
          </DialogHeader>
          {dryRunResult && (
            <div className="rounded-md border p-3 space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Planned states:</p>
              {Object.entries(dryRunResult.state_breakdown).map(([state, count]) => (
                <div key={state} className="flex justify-between text-sm">
                  <span>{state}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInitialize} disabled={initLoading}>
              {initLoading && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Confirm Initialize
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Failures Dialog */}
      <Dialog open={allFailuresOpen} onOpenChange={setAllFailuresOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>All Failures (Last 24h)</DialogTitle>
            <DialogDescription>
              {pf?.last_failures.length ?? 0} total failures
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto space-y-2 flex-1 pr-1">
            {pf?.last_failures.map((f) => (
              <div
                key={f.id}
                className="rounded border bg-muted/30 p-3 text-xs space-y-1"
              >
                <div className="flex justify-between items-start">
                  <span className="font-medium">{f.agent_name}</span>
                  <span className="text-muted-foreground shrink-0 ml-2">{f.updated_at}</span>
                </div>
                {f.patient_id && (
                  <p className="text-muted-foreground font-mono">{f.patient_id}</p>
                )}
                {f.error_message && (
                  <p className="text-muted-foreground whitespace-pre-wrap break-words">
                    {f.error_message}
                  </p>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Care Plan Generation Progress */}
      <CarePlanGenerationTracker orgId={orgId} />

      {/* Approved PCPs */}
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-3">
          <CardTitle className="text-base font-semibold">Approved PCPs</CardTitle>
          <div className="flex flex-wrap gap-2">
            {pcpDirty && (
              <Button
                size="sm"
                onClick={handleSavePcps}
                disabled={pcpSaving}
                className="gap-1.5"
              >
                {pcpSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Save className="h-3 w-3" />
                )}
                Save
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pcpLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  placeholder="Add provider name..."
                  value={newPcp}
                  onChange={(e) => setNewPcp(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addPcp()}
                  className="text-sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addPcp}
                  disabled={!newPcp.trim()}
                  className="shrink-0"
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              {pcpDraft.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-2">
                  No approved PCPs configured
                </p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {pcpDraft.map((pcp, i) => (
                    <Badge key={i} variant="secondary" className="gap-1.5 pr-1.5">
                      {pcp}
                      <button
                        onClick={() => removePcp(i)}
                        className="hover:text-destructive transition-colors"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Consent CSV Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Consent CSV Upload</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Upload a CSV with an <code className="bg-muted px-1 rounded">MRN</code> column to
              mark patients as consented.
            </p>
            <div className="flex gap-2 items-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="hidden"
                id="consent-csv-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={csvUploading}
                className="gap-1.5"
              >
                {csvUploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                Upload CSV
              </Button>
            </div>
            {csvResult && (
              <div className="rounded-md border bg-muted/30 p-3 space-y-1.5 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <span className="text-muted-foreground">Matched:</span>
                  <span className="font-medium">{csvResult.matched}</span>
                  <span className="text-muted-foreground">Newly consented:</span>
                  <span className="font-medium text-success">{csvResult.newly_consented}</span>
                  <span className="text-muted-foreground">Already consented:</span>
                  <span className="font-medium">{csvResult.already_consented}</span>
                  <span className="text-muted-foreground">Unmatched:</span>
                  <span className={cn("font-medium", csvResult.unmatched > 0 && "text-warning")}>
                    {csvResult.unmatched}
                  </span>
                </div>
                {csvResult.unmatched_mrns.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Unmatched MRNs:</p>
                    <div className="flex flex-wrap gap-1">
                      {csvResult.unmatched_mrns.map((mrn) => (
                        <Badge key={mrn} variant="outline" className="text-xs text-warning border-warning/30">
                          {mrn}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* QMB Eligibility Check */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            QMB Eligibility Check
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Runs the TPHM QMB eligibility scraper (stagehand) for consented patients.
              QMB status determines G-0557 vs G-0558 in billing. The scrape takes ~6+ hours
              and runs in the background.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                setQmbLoading(true)
                try {
                  await api.submitTphmEligibilityByDate(orgId)
                  onRefresh()
                } catch (err: any) {
                  // silent — user can check logs
                } finally {
                  setQmbLoading(false)
                }
              }}
              disabled={qmbLoading}
              className="gap-1.5"
            >
              {qmbLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ShieldCheck className="h-3.5 w-3.5" />
              )}
              {qmbLoading ? "Submitting…" : "Run QMB eligibility check"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Ingest from Drive */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <FolderOpen className="h-4 w-4" />
            Bulk Ingest from Drive
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Import Stage 3/4 simplified patient JSONs from a Google Drive folder.
              Each file will have PCP determined and care plan generated via LLM.
            </p>

            <div className="space-y-2">
              <Input
                placeholder="Google Drive folder URL or ID..."
                value={driveFolderId}
                onChange={(e) => setDriveFolderId(e.target.value)}
                className="text-sm"
              />
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={driveRecursive}
                    onChange={(e) => setDriveRecursive(e.target.checked)}
                    className="rounded border-border"
                  />
                  Recursive
                </label>
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Concurrency:</span>
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={driveMaxConcurrent}
                    onChange={(e) => setDriveMaxConcurrent(Number(e.target.value))}
                    className="w-20"
                  />
                  <span className="font-medium w-4 text-center">{driveMaxConcurrent}</span>
                </div>
              </div>
              <Button
                size="sm"
                onClick={handleDriveIngest}
                disabled={driveSubmitting || !driveFolderId.trim()}
                className="gap-1.5"
              >
                {driveSubmitting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Upload className="h-3.5 w-3.5" />
                )}
                Start Ingestion
              </Button>
            </div>

            {/* Active job progress */}
            {activeJob && (
              <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Job Progress</span>
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-xs",
                      activeJob.status === "completed" && "text-success border-success/30",
                      activeJob.status === "failed" && "text-destructive border-destructive/30",
                      (activeJob.status === "processing" || activeJob.status === "downloading") && "text-info border-info/30",
                      activeJob.status === "pending" && "text-warning border-warning/30",
                    )}
                  >
                    {activeJob.status === "processing" && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    {activeJob.status === "downloading" && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                    {activeJob.status}
                  </Badge>
                </div>

                {activeJob.total_files > 0 && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{activeJob.processed + activeJob.failed} / {activeJob.total_files}</span>
                      <span>
                        {activeJob.processed} ok
                        {activeJob.failed > 0 && <span className="text-destructive ml-1">{activeJob.failed} failed</span>}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full transition-all duration-500",
                          activeJob.failed > 0 ? "bg-warning" : "bg-success",
                        )}
                        style={{
                          width: `${Math.round(((activeJob.processed + activeJob.failed) / activeJob.total_files) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {activeJob.error_message && (
                  <p className="text-xs text-destructive">{activeJob.error_message}</p>
                )}

                {/* Per-patient results */}
                {activeJob.results.length > 0 && (
                  <Collapsible open={jobResultsOpen} onOpenChange={setJobResultsOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="gap-1.5 h-7 px-2 text-xs">
                        <Table className="h-3 w-3" />
                        {activeJob.results.length} results
                        <ChevronDown className={cn("h-3 w-3 transition-transform", jobResultsOpen && "rotate-180")} />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 max-h-60 overflow-y-auto space-y-1">
                        {activeJob.results.map((r, i) => (
                          <div
                            key={i}
                            className={cn(
                              "rounded border p-2 text-xs flex items-center justify-between",
                              r.status === "error" ? "bg-destructive/10 border-destructive/30" : "bg-muted/30",
                            )}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {r.status === "success" ? (
                                <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                              ) : (
                                <XCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
                              )}
                              <span className="font-medium truncate">{r.patient_name || r.filename}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-2">
                              {r.pcp && (
                                <Badge variant="secondary" className="text-[10px]">{r.pcp}</Badge>
                              )}
                              {r.error && (
                                <span className="text-destructive truncate max-w-[140px] sm:max-w-[200px]" title={r.error}>
                                  {r.error}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            )}

            {/* Recent jobs */}
            {recentJobs.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Recent Jobs</p>
                {recentJobs.slice(0, 5).map((j) => (
                  <div
                    key={j.job_id}
                    className="rounded border bg-muted/30 p-2 text-xs flex items-center justify-between cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      setActiveJobId(j.job_id)
                      setActiveJob(null)
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          "text-[10px]",
                          j.status === "completed" && "text-success border-success/30",
                          j.status === "failed" && "text-destructive border-destructive/30",
                        )}
                      >
                        {j.status}
                      </Badge>
                      <span>{j.processed}/{j.total_files} processed</span>
                      {j.failed > 0 && <span className="text-destructive">{j.failed} failed</span>}
                    </div>
                    <span className="text-muted-foreground">
                      {parseApiTimestamp(j.created_at)?.toLocaleDateString() ?? ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
